import React, { useEffect } from 'react';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import PreviewRoleContext from '../../context/PreviewRoleContext';

// Real desktop pages — the same components App.js routes to, so previewing / viewing-as
// shows the genuine screens (nav, dashboards, everything) with the target user's data.
import AdminDashboard from './AdminDashboard';
import AdminEmployees from './AdminEmployees';
import AdminEmployeeHub from './AdminEmployeeHub';
import AdminTimeOff from './AdminTimeOff';
import AdminCertifications from './AdminCertifications';
import AdminAsks from './AdminAsks';
import AdminProjects from './AdminProjects';
import AdminProjectDetail from './AdminProjectDetail';
import AdminPurchaseOrders from './AdminPurchaseOrders';
import AdminConversations from './AdminConversations';
import AdminTimesheets from './AdminTimesheets';
import AdminSafety from './AdminSafetyHub';
import AdminVendors from './AdminVendors';
import AdminBids from './AdminBids';
import AdminBidDetail from './AdminBidDetail';
import AdminClients from './AdminClients';
import AdminNewProjects from './AdminNewProjects';
import AdminNewProjectDetail from './AdminNewProjectDetail';
import AdminWorkflows from './AdminWorkflows';
import AdminSettings from './AdminSettings';
import AdminMaterials from './AdminMaterials';
import AdminCategories from './AdminCategories';
import AdminDirectives from './AdminDirectives';
import AdminStock from './AdminStock';
import AdminEquipment from './AdminEquipment';
import AdminAdministration from './AdminAdministration';
import AdminPermits from './AdminPermits';
import AdminShop from './AdminShop';
import PMDashboard from '../portal/PMDashboard';
import CompanyConversations from '../portal/CompanyConversations';

// Roles that have a desktop layout. Field/Shop/Field-Supervisor are mobile-only;
// 'admin' is the owner login, not an employee role.
export const DESKTOP_ROLES = ['Executive', 'ProjectManager', 'Accounting'];

// Where each role lands on the desktop after login (mirrors services/viewMode desktopHome).
export function homeFor(role) {
  if (role === 'ProjectManager') return '/admin/pm-dashboard';
  if (role === 'Accounting')     return '/admin/timesheets';
  return '/admin'; // Executive / owner
}

// Patch window.fetch while mounted so the target app's API calls carry the
// x-impersonate-user header (server honors it read-only for owner/Executive). The
// ref lets the caller change the target without re-patching.
export function useImpersonateFetch(idRef) {
  useEffect(() => {
    const orig = window.fetch;
    window.fetch = function (url, opts = {}) {
      try {
        const id = idRef.current;
        const u = typeof url === 'string' ? url : (url && url.url) || '';
        if (id && u.indexOf('/api') !== -1) opts = { ...opts, headers: { ...(opts.headers || {}), 'x-impersonate-user': id } };
      } catch {}
      return orig.call(this, url, opts);
    };
    return () => { window.fetch = orig; };
  }, [idRef]);
}

function Placeholder({ label }) {
  return (
    <AdminLayout>
      <div style={{ padding: 40, color: '#6b7280', fontSize: 15 }}>
        <strong>{label}</strong> isn't shown here (it would nest inside itself).
      </div>
    </AdminLayout>
  );
}

// A self-contained desktop app: its own MemoryRouter + preview-role context so the
// sidebar/nav render as the target's role and clicks stay inside this subtree.
export default function ImpersonatedApp({ role, startPath }) {
  return (
    <PreviewRoleContext.Provider value={role}>
      <MemoryRouter initialEntries={[startPath || '/admin']}>
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/pm-dashboard" element={<PMDashboard />} />
          <Route path="/admin/directives" element={<AdminDirectives />} />
          <Route path="/admin/employees" element={<AdminEmployeeHub />} />
          <Route path="/admin/employees/list" element={<AdminEmployees />} />
          <Route path="/admin/employees/time-off" element={<AdminTimeOff />} />
          <Route path="/admin/employees/certifications" element={<AdminCertifications />} />
          <Route path="/admin/send-asks" element={<AdminAsks />} />
          <Route path="/admin/projects" element={<AdminProjects />} />
          <Route path="/admin/projects/:id" element={<AdminProjectDetail />} />
          <Route path="/admin/purchase-orders" element={<AdminPurchaseOrders />} />
          <Route path="/admin/conversations" element={<AdminConversations />} />
          <Route path="/admin/company-conversations" element={<CompanyConversations />} />
          <Route path="/admin/timesheets" element={<AdminTimesheets />} />
          <Route path="/admin/safety" element={<AdminSafety />} />
          <Route path="/admin/vendors" element={<AdminVendors />} />
          <Route path="/admin/bids" element={<AdminBids />} />
          <Route path="/admin/bids/:id" element={<AdminBidDetail />} />
          <Route path="/admin/new-projects" element={<AdminNewProjects />} />
          <Route path="/admin/new-projects/:id" element={<AdminNewProjectDetail />} />
          <Route path="/admin/workflows" element={<AdminWorkflows />} />
          <Route path="/admin/workflows/:type" element={<AdminWorkflows />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/materials" element={<AdminMaterials />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/stock" element={<AdminStock />} />
          <Route path="/admin/equipment" element={<AdminEquipment />} />
          <Route path="/admin/administration" element={<AdminAdministration />} />
          <Route path="/admin/permits" element={<AdminPermits />} />
          <Route path="/admin/shop" element={<AdminShop />} />
          <Route path="/admin/clients" element={<AdminClients />} />
          <Route path="/admin/trello" element={<Navigate to="/admin" replace />} />
          {/* Preview/view-as pages are excluded to avoid nesting. */}
          <Route path="/admin/mobile-preview" element={<Placeholder label="Mobile Preview" />} />
          <Route path="/admin/desktop-preview" element={<Placeholder label="Desktop Preview" />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </MemoryRouter>
    </PreviewRoleContext.Provider>
  );
}
